// path: src/utils/ipaProductAccess.ts
import { User } from '../@types';
import type { IpaRouteAccess } from './ipaAccess';

// Caminhos e adicionais repetem os de ipaAccess.ts, que importa este modulo; o teste os compara.

const PRICING_HOME_PATH = '/ipa/gerenciador-de-precos';
const NEGOTIATION_HOME_PATH = '/ipa/negociacoes/fornecedor';
const DEMAND_SIMULATOR_HOME_PATH = '/ipa/calculadora-demanda';
const LOGIN_PATH = '/login';

const CAMPAIGNS_ADDITIONAL = 'CAMPANHAS_PROMOCIONAIS';
const PRICE_OPTIMIZATION_ADDITIONAL = 'OTIMIZACAO_DE_PRECOS';
const DEMAND_FORECAST_ADDITIONAL = 'PREVISAO_DEMANDA';
const PRICE_ARCHITECTURE_ADDITIONAL = 'ARQUITETURA_PRECOS';

// O gateway recusa a Precificacao com pacote LITE gravado; o front acompanha.
const REFUSED_PRICING_PACKAGE = 'LITE';

export type ProductRuleAccess = {
  hasPricing: boolean;
  hasNegotiation: boolean;
  clientHasPricing: boolean;
  clientHasNegotiation: boolean;
};

const hasOtherService = (user: User | null | undefined, serviceName: string) =>
  !!user?.servicos?.some(service => service.name === serviceName) || !!user?.servicosCliente?.some(service => service.name === serviceName);

const hasAdditional = (user: User | null | undefined, contract: string) => (user?.tipoAdicionalContratos?.IPA ?? []).includes(contract);

export const hasNegotiation = (user?: User | null) =>
  user?.produtosIpa?.negociacaoAtivaNoCliente === true && user?.produtosIpa?.negociacaoAtivaNoVinculo === true;

export const hasPricing = (user?: User | null) =>
  user?.produtosIpa?.precificacaoAtivaNoCliente === true &&
  user?.produtosIpa?.precificacaoAtivaNoVinculo === true &&
  user?.servicoIpa?.pacoteIpa !== REFUSED_PRICING_PACKAGE;

export const clientHasNegotiation = (user?: User | null) => user?.produtosIpa?.negociacaoAtivaNoCliente === true;

export const clientHasPricing = (user?: User | null) => user?.produtosIpa?.precificacaoAtivaNoCliente === true;

export const hasAnyIpaProduct = (user?: User | null) => hasNegotiation(user) || hasPricing(user);

export const getProductRuleAccess = (user?: User | null): ProductRuleAccess => ({
  hasPricing: hasPricing(user),
  hasNegotiation: hasNegotiation(user),
  clientHasPricing: clientHasPricing(user),
  clientHasNegotiation: clientHasNegotiation(user),
});

export const canAccessProductRoute = (user: User | null | undefined, routeAccess?: IpaRouteAccess) => {
  if (!routeAccess) return true;
  if (!user) return false;
  if (routeAccess === 'NEGOTIATION') return hasNegotiation(user);
  if (routeAccess === 'CAMPAIGNS') return hasPricing(user) && hasAdditional(user, CAMPAIGNS_ADDITIONAL);
  if (routeAccess === 'PRICE_OPTIMIZATION') return hasPricing(user) && hasAdditional(user, PRICE_OPTIMIZATION_ADDITIONAL);
  if (routeAccess === 'DEMAND_FORECAST') return hasPricing(user) && hasAdditional(user, DEMAND_FORECAST_ADDITIONAL);
  if (routeAccess === 'PRICE_ARCHITECTURE') return hasPricing(user) && hasAdditional(user, PRICE_ARCHITECTURE_ADDITIONAL);
  if (routeAccess === 'SDD') return hasOtherService(user, 'SDD');
  return hasPricing(user);
};

export const getFirstOtherProductPath = (user?: User | null): string | null => {
  if (hasOtherService(user, 'SDD')) return DEMAND_SIMULATOR_HOME_PATH;
  if (hasOtherService(user, 'PAN')) return user?.servicoPanel?.pacotePainel?.name === 'FREE' ? '/isa/painel-geral-free' : '/isa/painel-geral';
  if (hasOtherService(user, 'COL')) return '/isa/historico-preco';
  if (hasOtherService(user, 'IRA')) return '/ira';
  if (hasOtherService(user, 'BI') && user?.servicoBi?.analises?.length) return `/analise/${user.servicoBi.analises[0]?.codigo}`;
  if (hasOtherService(user, 'BI') && user?.servicoBi?.graficos?.length) return `/acompanhamento/${user.servicoBi.graficos[0]?.codigo}`;
  return null;
};

export const getProductFallbackPath = (user: User | null | undefined, routeAccess?: IpaRouteAccess): string => {
  if (routeAccess !== 'PRO' && hasPricing(user)) return PRICING_HOME_PATH;
  if (routeAccess !== 'NEGOTIATION' && hasNegotiation(user)) return NEGOTIATION_HOME_PATH;
  return getFirstOtherProductPath(user) ?? LOGIN_PATH;
};

export const getProductHomePath = (user?: User | null): string | null => {
  if (hasPricing(user)) return PRICING_HOME_PATH;
  if (hasNegotiation(user)) return NEGOTIATION_HOME_PATH;
  return null;
};
